import { useContext, useEffect, useState } from "react";
import { CodedProperty, getById, getByIds, NamedProperty, Optional, ValidationResult } from "sprint-common-web";

import { CreateMovieVersionRequest } from "../../../content/movie/api/CreateMovieVersionRequest";
import { CreateEpisodeVersionRequest } from "../../../content/tv-series/api/CreateEpisodeVersionRequest";
import { CreateEntityResponse } from "../../api/common/CreateEntityResponse";
import { useAirlines } from "../../data/data-hooks/useArilines";
import { useNonTrailerVersionTypes } from "../../data/data-hooks/useNonTrailerVersionTypes";
import { useTrailerVersionTypes } from "../../data/data-hooks/useTrailerVersionTypes";
import { DistributorsDataContext } from "../../data/lookup-data-providers/DistributorsDataProvider";
import { ContentReferenceType } from "../../types/ContentReferenceType";
import {
    ContentVersionDetails,
    CreateContentVersionParams,
    ensureCreateMovieContentVersionParams,
    ensureCreateTvEpisodeContentVersionParams
} from "./ContentVersion";
import { CreateContentVersionFormInputs } from "./CreateContentVersionForm";
import { useContentVersion } from "./useContentVersion";

export const useCreateContentVersionForm = ({ content }: { content: CreateContentVersionParams }) => {
    const [trailersOnly, setTrailersOnly] = useState<boolean>(false);
    const [versionTypes, setVersionTypes] = useState<NamedProperty[]>([]);
    const [validationResult, setValidationResult] = useState<ValidationResult>();

    const { data: trailerVersionTypes } = useTrailerVersionTypes();
    const { data: nonTrailerVersionTypes } = useNonTrailerVersionTypes();
    const { data: airlines } = useAirlines();
    const { getDistributorById } = useContext(DistributorsDataContext);
    const {
        createEpisodeVersion,
        createMovieVersion,
        validateCreateEpisodeVersion,
        validateCreateMovieVersion
    } = useContentVersion();

    useEffect(() => {
        if (trailersOnly) {
            setVersionTypes(trailerVersionTypes ?? []);
        } else {
            setVersionTypes(nonTrailerVersionTypes ?? []);
        }
    }, [trailersOnly, trailerVersionTypes, nonTrailerVersionTypes]);

    const handleCreateMovieVersion = async ({
        inputs,
        brandings,
        versionType
    }: {
        inputs: CreateContentVersionFormInputs;
        brandings: CodedProperty[];
        versionType: NamedProperty;
    }): Promise<Optional<CreateEntityResponse>> => {
        ensureCreateMovieContentVersionParams(content);

        const validationResponse = await validateCreateMovieVersion({
            movieId: content.movieId,
            distributorId: inputs.distributorId,
            typeId: versionType.id,
            brandingIds: brandings.map((branding) => branding.id)
        });

        setValidationResult(validationResponse);

        if (!validationResponse.isValid) {
            return;
        }

        const request: CreateMovieVersionRequest = {
            distributorId: inputs.distributorId,
            typeId: inputs.versionTypeId,
            brandingIds: inputs.brandingIds
        };

        return createMovieVersion(request, content.movieId);
    };

    const handleCreateTvEpisodeVersion = async ({
        inputs,
        brandings,
        versionType,
        distributor
    }: {
        inputs: CreateContentVersionFormInputs;
        brandings: CodedProperty[];
        versionType: NamedProperty;
        distributor: NamedProperty;
    }): Promise<Optional<CreateEntityResponse>> => {
        ensureCreateTvEpisodeContentVersionParams(content);

        const validationResponse = await validateCreateEpisodeVersion({
            tvSeriesId: content.tvSeriesId,
            episodeId: content.episodeId,
            distributorId: distributor.id,
            typeId: versionType.id,
            brandingIds: brandings.map((branding) => branding.id)
        });

        setValidationResult(validationResponse);

        if (!validationResponse.isValid) {
            return;
        }

        const request: CreateEpisodeVersionRequest = {
            tvSeriesId: content.tvSeriesId,
            distributorId: distributor.id,
            typeId: versionType.id,
            code: inputs.code,
            brandingIds: brandings?.map((branding) => branding.id)
        };

        return createEpisodeVersion(request, content.episodeId);
    };

    const resolveCreateContentVersion = ({
        distributor,
        versionType,
        brandings,
        inputs
    }: {
        distributor: NamedProperty;
        versionType: NamedProperty;
        brandings: CodedProperty[];
        code: string;
        inputs: CreateContentVersionFormInputs;
    }) => {
        switch (content.type) {
            case ContentReferenceType.Movie:
                return () => handleCreateMovieVersion({ inputs, brandings, versionType });

            case ContentReferenceType.TvSeries:
                return () => handleCreateTvEpisodeVersion({ inputs, brandings, versionType, distributor });

            default:
                throw new Error("Unsupported content type");
        }
    };

    const createNewContentVersion = async (
        inputs: CreateContentVersionFormInputs
    ): Promise<Optional<ContentVersionDetails>> => {
        const distributor = await getDistributorById(inputs.distributorId);
        const versionType = getById(versionTypes, inputs.versionTypeId);
        const brandings = airlines ? getByIds(airlines, inputs.brandingIds) : [];
        const code = inputs.code;

        const createContentVersion = resolveCreateContentVersion({
            distributor,
            versionType,
            brandings,
            code,
            inputs
        });

        const response = await createContentVersion();

        if (!response) {
            return;
        }

        return {
            id: response?.entityId,
            distributor,
            code: code,
            type: versionType,
            brandings,
            isTrailer: trailersOnly
        };
    };

    return {
        airlines,
        versionTypes,
        createNewContentVersion,
        trailersOnly,
        setTrailersOnly,
        validationResult,
        setValidationResult
    };
};
